import React, { useState } from "react";
import { Box, Typography } from "@mui/material";

interface ImageViewerProps {
	images: string[];
	height?: number;
}

export const ImageViewer: React.FC<ImageViewerProps> = ({
	images,
	height = 200,
}) => {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [hoverIndex, setHoverIndex] = useState<number | null>(null);

	// The displayed image: hover preview takes priority, otherwise the selected/clicked image
	const displayIndex = hoverIndex !== null ? hoverIndex : selectedIndex;

	if (!images || images.length === 0) {
		return (
			<Box
				sx={{
					width: "100%",
					height,
					backgroundColor: "#f0f0f0",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					borderRadius: 1,
				}}
			>
				<Typography variant="body2" color="text.secondary">
					No Image
				</Typography>
			</Box>
		);
	}

	const buildUrl = (filename: string) =>
		`${process.env.REACT_APP_COMPANY_LOGO_URL}/${filename}`;

	return (
		<Box sx={{ width: "100%" }}>
			{/* Main image */}
			<Box
				sx={{
					width: "100%",
					height,
					backgroundColor: "#f5f5f5",
					borderRadius: 1,
					overflow: "hidden",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Box
					component="img"
					src={buildUrl(images[displayIndex])}
					alt={`Image ${displayIndex + 1}`}
					sx={{
						maxWidth: "100%",
						maxHeight: "100%",
						objectFit: "contain",
					}}
				/>
			</Box>

			{/* Thumbnails — only if more than 1 image */}
			{images.length > 1 && (
				<Box
					sx={{
						display: "flex",
						gap: 0.75,
						mt: 1,
						overflowX: "auto",
						pb: 0.5,
					}}
				>
					{images.map((img, idx) => (
						<Box
							key={img}
							onClick={() => setSelectedIndex(idx)}
							onMouseEnter={() => setHoverIndex(idx)}
							onMouseLeave={() => setHoverIndex(null)}
							sx={{
								width: 48,
								height: 48,
								minWidth: 48,
								borderRadius: 0.5,
								overflow: "hidden",
								cursor: "pointer",
								border: "2px solid",
								borderColor:
									idx === selectedIndex
										? "secondary.main"
										: idx === hoverIndex
											? "grey.400"
											: "transparent",
								backgroundColor: "#f5f5f5",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								transition: "border-color 0.15s ease",
							}}
						>
							<Box
								component="img"
								src={buildUrl(img)}
								alt={`Thumb ${idx + 1}`}
								sx={{
									maxWidth: "100%",
									maxHeight: "100%",
									objectFit: "contain",
								}}
							/>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};
